import ClassNames from "classnames";
import { FunctionComponent, useRef, useState } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import styles from "../stylesheets/components/left-overlay-nav.module.css";
import mainMenuStyles from "../stylesheets/components/main-menu.module.css";
import { Language, MainMenuItem, PageState } from "../types/index.js";
import { onTransitionEnd } from "../utils/utils.js";
import LeftOverlayNavItemTopLevel from "./LeftOverlayNavItemTopLevel.js";
import LeftOverlaySubmenu from "./LeftOverlaySubmenu.js";

interface Props {
  isPreview: boolean;
  pages: PageState;
  languageId: Language;
  fallbackLanguageId: Language | undefined;
  activePagePath: string[];
  menuItems: MainMenuItem[];
  showMenuSeparators: boolean;
}

const LeftOverlayNav: FunctionComponent<Props> = ({
  isPreview,
  pages,
  languageId,
  fallbackLanguageId,
  activePagePath,
  menuItems,
  showMenuSeparators,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const openIndexRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);

  const currentMenuItem = menuItems[openIndexRef.current];

  return (
    <div
      className={ClassNames(
        styles.LeftOverlayNav,
        mainMenuStyles.MainMenu__LeftOverlayNav,
        {
          [mainMenuStyles["MainMenu__LeftOverlayNav--show-menu-separators"]]:
            showMenuSeparators,
        },
      )}
    >
      <SwitchTransition>
        <CSSTransition
          key={String(isOpen)}
          nodeRef={nodeRef}
          addEndListener={(done) => {
            nodeRef.current && onTransitionEnd(nodeRef.current, done);
          }}
          classNames={styles.LeftOverlayNav__MenuContainer}
        >
          <div className={styles.LeftOverlayNav__MenuContainer} ref={nodeRef}>
            {!isOpen && (
              <nav
                className={ClassNames(mainMenuStyles.MainMenu__LeftOverlayNav)}
              >
                <ul
                  className={ClassNames(
                    mainMenuStyles.MainMenu__List,
                    mainMenuStyles["MainMenu__List--level0"],
                  )}
                >
                  {menuItems.map((menuItem, index) => (
                    <LeftOverlayNavItemTopLevel
                      key={menuItem.id}
                      activePagePath={activePagePath}
                      currentMenuItem={menuItem}
                      fallbackLanguageId={fallbackLanguageId}
                      isPreview={isPreview}
                      languageId={languageId}
                      pages={pages}
                      parentId={menuItem.id}
                      onOpen={() => {
                        openIndexRef.current = index;
                        setIsOpen(true);
                      }}
                    />
                  ))}
                </ul>
              </nav>
            )}

            {isOpen && currentMenuItem && (
              <LeftOverlaySubmenu
                activePagePath={activePagePath}
                fallbackLanguageId={fallbackLanguageId}
                isPreview={isPreview}
                languageId={languageId}
                currentMenuItem={currentMenuItem}
                onClose={() => setIsOpen(false)}
                pages={pages}
              />
            )}
          </div>
        </CSSTransition>
      </SwitchTransition>
    </div>
  );
};

export default LeftOverlayNav;
